import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Button, 
  Row, 
  Col, 
  Badge,
  Table,
  Spinner,
  Alert,
  Tabs,
  Tab,
  Form,
  Tooltip,
  OverlayTrigger
} from 'react-bootstrap';
import { 
  fetchStream, 
  updateStream,
  fetchStreams,
  regenerateStreamKey
} from '../../../store/actions/StreamActions';
import swal from 'sweetalert';
import { selectIsAdmin } from '../../../store/selectors/AuthSelectors';
import CaptionManager from '../../components/Common/CaptionManager';
import SocialStreamingConfig from './SocialStreamingConfig';
import StreamWatermark from './StreamWatermark';
import StreamUnavailable from './StreamUnavailable';
import { Button as Btn } from '../../ui';

const StreamDetail = ({ 
  selectedStream, 
  loading, 
  error, 
  updating,
  regenerating,
  isAdmin,
  fetchStream,
  updateStream,
  fetchStreams,
  regenerateStreamKey
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const hlsCleanupRef = useRef(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [copiedField, setCopiedField] = useState(null);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [showCaptionModal, setShowCaptionModal] = useState(false);
  const [embedOptions, setEmbedOptions] = useState({
    width: '100%',
    height: '480',
    autoplay: true,
    muted: true,
    logo: true
  });

  useEffect(() => {
    fetchStream(id);
    
    // Refresh every 30 seconds (reduced frequency to ease server load)
    const interval = setInterval(() => {
      fetchStream(id);
    }, 30000);

    return () => clearInterval(interval);
  }, [id, fetchStream]);

  useEffect(() => {
    if (selectedStream) {
      setEditForm({
        name: selectedStream.name || '',
        description: selectedStream.description || ''
      });
    }
  }, [selectedStream]);

  const [selectedCaption, setSelectedCaption] = useState('off');
  const [playerStatus, setPlayerStatus] = useState('loading'); // 'loading'|'live'|'unavailable'

  // Initialize HLS video player
  useEffect(() => {
    const loadHls = async () => {
      if (!selectedStream?.hls_url || !videoRef.current) return;
      
      const { attachHls } = await import('../../../utils/hlsPlayer');
      const video = videoRef.current;
      const hlsUrl = selectedStream.hls_url;

      // Clear existing caption tracks
      while (video.querySelector('track')) {
        video.querySelector('track').remove();
      }

      setPlayerStatus('loading');
      hlsCleanupRef.current = await attachHls(video, hlsUrl, { onStatusChange: setPlayerStatus });

      // Add caption tracks if available
      if (selectedStream?.captions && selectedStream.captions.length > 0) {
        selectedStream.captions.forEach((caption, index) => {
          const track = document.createElement('track');
          track.kind = 'subtitles';
          track.label = caption.label;
          track.srclang = caption.language;
          track.src = caption.url;
          track.default = caption.isDefault || false;
          video.appendChild(track);
        });
      }
    };

    if (selectedStream?.hls_url) {
      loadHls();
    }
    return () => { try { hlsCleanupRef.current && hlsCleanupRef.current(); } catch (_) {} };
  }, [selectedStream?.hls_url, selectedStream?.captions]);

  const handleCaptionChange = (e) => {
    const value = e.target.value;
    setSelectedCaption(value);
    
    const video = videoRef.current;
    if (!video) return;
    
    // Disable all tracks first
    for (let i = 0; i < video.textTracks.length; i++) {
      video.textTracks[i].mode = 'disabled';
    }
    
    // Enable selected track
    if (value !== 'off') {
      const trackIndex = parseInt(value);
      if (video.textTracks[trackIndex]) {
        video.textTracks[trackIndex].mode = 'showing';
      }
    }
  };

  const copyToClipboard = (text, label, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      swal("Copied!", `${label} copied to clipboard.`, "success", {
        timer: 1500,
        buttons: false
      });
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const renderCopyButton = (text, label, field) => (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{copiedField === field ? '✓ Copied!' : `Copy ${label}`}</Tooltip>}
    >
      <Button 
        variant={copiedField === field ? "success" : "outline-primary"} 
        size="sm"
        onClick={() => copyToClipboard(text, label, field)}
      >
        <i className={`fas fa-${copiedField === field ? 'check' : 'copy'}`}></i>
      </Button>
    </OverlayTrigger>
  );

  const getStatusBadge = (status, isLive) => {
    if (status === 'failover') {
      return <Badge className="px-3 py-2" style={{ background: '#2563eb', color: '#fff' }}>● Failover</Badge>;
    }
    if (isLive) {
      return <Badge bg="success" className="px-3 py-2">● Live</Badge>;
    }

    switch (status) {
      case 'broadcasting':
        return <Badge bg="success" className="px-3 py-2">● Broadcasting</Badge>;
      case 'active':
        return <Badge bg="primary" className="px-3 py-2">Active</Badge>;
      case 'inactive':
        return <Badge bg="secondary" className="px-3 py-2">Inactive</Badge>;
      case 'disconnected':
        return <Badge bg="warning" className="px-3 py-2">Disconnected</Badge>;
      default:
        return <Badge bg="secondary" className="px-3 py-2">{status}</Badge>;
    }
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    updateStream(id, editForm, (err) => {
      if (!err) {
        swal("Updated!", "Stream details updated successfully.", "success");
        setIsEditing(false);
        fetchStream(id);
      } else {
        swal("Error!", "Failed to update stream.", "error");
      }
    });
  };

  const handleRegenerateKey = () => {
    swal({
      title: "Regenerate Stream Key?",
      text: "This will generate a new stream key. The old key will no longer work!",
      icon: "warning",
      buttons: ["Cancel", "Regenerate"],
      dangerMode: true,
    }).then((willRegenerate) => {
      if (willRegenerate) {
        regenerateStreamKey(id, (err) => {
          if (!err) {
            swal("Success!", "Stream key has been regenerated.", "success");
            fetchStream(id);
          } else {
            swal("Error!", "Failed to regenerate stream key.", "error");
          }
        });
      }
    });
  };

  const generateEmbedCode = () => {
    const streamName = selectedStream?.stream_name || selectedStream?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'stream';
    const params = new URLSearchParams();
    params.set('stream', streamName);
    if (!embedOptions.autoplay) params.set('autoplay', 'false');
    if (!embedOptions.muted) params.set('muted', 'false');
    if (!embedOptions.logo) params.set('logo', 'false');
    
    const embedUrl = `${window.location.origin}/embed-player.html?${params.toString()}`;
    
    return `<iframe 
  src="${embedUrl}" 
  width="${embedOptions.width}" 
  height="${embedOptions.height}" 
  frameborder="0" 
  allowfullscreen
  allow="autoplay; fullscreen">
</iframe>`;
  };

  const copyEmbedCode = () => {
    const code = generateEmbedCode();
    navigator.clipboard.writeText(code).then(() => {
      swal("Copied!", "Embed code copied to clipboard!", "success", {
        timer: 1500,
        buttons: false
      });
    });
  };

  if (loading && !selectedStream) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading stream details...</p>
      </div>
    );
  }

  if (!selectedStream && !loading) {
    return (
      <Alert variant="warning">
        <Alert.Heading>Stream Not Found</Alert.Heading>
        <p>The requested stream could not be found.</p>
        <Button variant="primary" onClick={() => navigate('/streams')}>
          Back to Streams
        </Button>
      </Alert>
    );
  }

  return (
    <>
      <button
        className="tw-mb-3 tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-content-muted hover:tw-text-heading"
        onClick={() => navigate('/streams')}
      >
        <i className="fa-solid fa-arrow-left" /> Back to Streams
      </button>
      <div className="tw-mb-6 tw-flex tw-flex-wrap tw-items-start tw-gap-4">
        <div className="tw-mr-auto">
          <h1 className="tw-flex tw-items-center tw-gap-3 tw-text-2xl tw-font-bold tw-text-heading">
            {selectedStream?.name}
            {getStatusBadge(selectedStream?.status, selectedStream?.is_live)}
          </h1>
          <p className="tw-mt-1 tw-text-sm tw-text-content-muted">
            {selectedStream?.description || 'No description'}
          </p>
          {isAdmin && (() => {
            // A team member's stream is attributed to the paying parent
            // account (parent_* is only ever set for a team member's stream).
            const s = selectedStream || {};
            const hasParent = s.parent_name || s.parent_company || s.parent_username || s.parent_email;
            const primary = hasParent
              ? (s.parent_name || s.parent_company || s.parent_username)
              : (s.owner_name || s.owner_company || s.owner_username);
            const email = hasParent ? s.parent_email : s.owner_email;
            const member = hasParent ? (s.owner_name || s.owner_username || s.owner_email) : null;
            return (
              <p className="tw-mt-1 tw-text-sm tw-text-content-muted">
                <i className="fa-solid fa-user tw-mr-1 tw-text-content-faint" />
                Owner:{' '}
                {(primary || email) ? (
                  <span className="tw-font-semibold tw-text-heading">
                    {primary}
                    {email && <span className="tw-ml-2 tw-font-normal tw-text-content-faint">{email}</span>}
                    {member && <span className="tw-ml-2 tw-font-normal tw-text-brand">via {member} (team)</span>}
                  </span>
                ) : <span className="tw-font-semibold tw-text-warn">Unassigned</span>}
              </p>
            );
          })()}
        </div>
        <div className="tw-flex tw-gap-2">
          <Btn variant="outline" size="sm" icon={isEditing ? 'fa-xmark' : 'fa-pen'} onClick={() => setIsEditing(!isEditing)}>
            {isEditing ? 'Cancel' : 'Edit'}
          </Btn>
          <Btn variant="secondary" size="sm" icon="fa-list" onClick={() => navigate('/streams')}>
            All Streams
          </Btn>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible className="mb-4">
          {error}
        </Alert>
      )}

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
      >
        <Tab eventKey="overview" title="Overview">
          <Row>
            <Col lg={8}>
              {isEditing ? (
                <Card className="mb-4">
                  <Card.Body>
                    <h5 className="mb-4">Edit Stream</h5>
                    <Form onSubmit={handleEditSubmit}>
                      <Form.Group className="mb-3">
                        <Form.Label>Stream Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                          required
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={editForm.description}
                          onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                        />
                      </Form.Group>
                      <div className="d-flex gap-2">
                        <Button 
                          variant="secondary" 
                          onClick={() => setIsEditing(false)}
                        >
                          Cancel
                        </Button>
                        <Button 
                          variant="primary" 
                          type="submit"
                          disabled={updating}
                        >
                          {updating ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Saving...
                            </>
                          ) : (
                            'Save Changes'
                          )}
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              ) : null}

              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="fas fa-link me-2"></i>
                    Stream URLs
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Table borderless>
                    <tbody>
                      <tr>
                        <td className="fw-bold" style={{width: '150px'}}>RTMP URL:</td>
                        <td>
                          <code className="bg-light px-2 py-1 rounded d-block mb-2">
                            {selectedStream?.rtmp_url}
                          </code>
                          <small className="text-muted">
                            Use this URL in OBS Studio or other broadcasting software
                          </small>
                        </td>
                        <td style={{width: '100px'}}>
                          {renderCopyButton(selectedStream?.rtmp_url, 'RTMP URL', 'rtmp')}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Stream Key:</td>
                        <td>
                          <code className="bg-light px-2 py-1 rounded d-block mb-2">
                            {selectedStream?.stream_key}
                          </code>
                          <small className="text-muted">
                            Unique identifier for this stream
                          </small>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            {renderCopyButton(selectedStream?.stream_key, 'Stream Key', 'key')}
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>Regenerate stream key</Tooltip>}
                            >
                              <Button 
                                variant="outline-warning" 
                                size="sm"
                                onClick={handleRegenerateKey}
                                disabled={regenerating}
                              >
                                <i className={`fas fa-sync-alt ${regenerating ? 'fa-spin' : ''}`}></i>
                              </Button>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">HLS URL (Primary):</td>
                        <td>
                          <code className="bg-light px-2 py-1 rounded d-block mb-2">
                            {selectedStream?.hls_url}
                          </code>
                          <small className="text-muted">
                            Primary HLS stream URL for playback
                          </small>
                        </td>
                        <td>
                          {renderCopyButton(selectedStream?.hls_url, 'HLS URL', 'hls')}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">HLS URL (Backup):</td>
                        <td>
                          <code className="bg-light px-2 py-1 rounded d-block mb-2">
                            {selectedStream?.hls_backup_url || selectedStream?.hls_url?.replace('.m3u8', '_backup.m3u8')}
                          </code>
                          <small className="text-muted">
                            Backup HLS stream URL (lower quality)
                          </small>
                        </td>
                        <td>
                          {renderCopyButton(selectedStream?.hls_backup_url || selectedStream?.hls_url?.replace('.m3u8', '_backup.m3u8'), 'HLS Backup URL', 'hls_backup')}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">DASH URL:</td>
                        <td>
                          <code className="bg-light px-2 py-1 rounded d-block mb-2">
                            {selectedStream?.dash_url || selectedStream?.hls_url?.replace('/hls/', '/dash/').replace('.m3u8', '.mpd')}
                          </code>
                          <small className="text-muted">
                            MPEG-DASH format for modern browsers
                          </small>
                        </td>
                        <td>
                          {renderCopyButton(selectedStream?.dash_url || selectedStream?.hls_url?.replace('/hls/', '/dash/').replace('.m3u8', '.mpd'), 'DASH URL', 'dash')}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Audio Only (MP3):</td>
                        <td>
                          <code className="bg-light px-2 py-1 rounded d-block mb-2">
                            {selectedStream?.audio_url || selectedStream?.hls_url?.replace('/hls/', '/audio/').replace('.m3u8', '.mp3')}
                          </code>
                          <small className="text-muted">
                            Audio-only stream for low bandwidth
                          </small>
                        </td>
                        <td>
                          {renderCopyButton(selectedStream?.audio_url || selectedStream?.hls_url?.replace('/hls/', '/audio/').replace('.m3u8', '.mp3'), 'Audio URL', 'audio')}
                        </td>
                      </tr>
                      <tr className="table-secondary">
                        <td colSpan="3" className="fw-bold text-center">
                          <i className="fas fa-layer-group me-2"></i>
                          Multi-Bitrate Streams (Adaptive Quality)
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Master Playlist (ABR):</td>
                        <td>
                          <code className="bg-light px-2 py-1 rounded d-block mb-2">
                            {selectedStream?.hls_master_url || selectedStream?.hls_url?.replace('index.m3u8', 'master.m3u8')}
                          </code>
                          <small className="text-muted">
                            Auto-switching quality based on bandwidth (Recommended)
                          </small>
                        </td>
                        <td>
                          {renderCopyButton(selectedStream?.hls_master_url || selectedStream?.hls_url?.replace('index.m3u8', 'master.m3u8'), 'Master Playlist URL', 'master')}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">SD 480p:</td>
                        <td>
                          <code className="bg-light px-2 py-1 rounded d-block mb-2">
                            {selectedStream?.hls_sd_url || selectedStream?.hls_url?.replace('index.m3u8', 'sd.m3u8')}
                          </code>
                          <small className="text-muted">
                            Standard Definition 480p (~800 kbps) - Mobile friendly
                          </small>
                        </td>
                        <td>
                          {renderCopyButton(selectedStream?.hls_sd_url || selectedStream?.hls_url?.replace('index.m3u8', 'sd.m3u8'), 'SD URL', 'sd')}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">HD 720p:</td>
                        <td>
                          <code className="bg-light px-2 py-1 rounded d-block mb-2">
                            {selectedStream?.hls_hd_url || selectedStream?.hls_url?.replace('index.m3u8', 'hd.m3u8')}
                          </code>
                          <small className="text-muted">
                            High Definition 720p (~2.5 Mbps) - Good for most devices
                          </small>
                        </td>
                        <td>
                          {renderCopyButton(selectedStream?.hls_hd_url || selectedStream?.hls_url?.replace('index.m3u8', 'hd.m3u8'), 'HD URL', 'hd')}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">FHD 1080p:</td>
                        <td>
                          <code className="bg-light px-2 py-1 rounded d-block mb-2">
                            {selectedStream?.hls_fhd_url || selectedStream?.hls_url?.replace('index.m3u8', 'fhd.m3u8')}
                          </code>
                          <small className="text-muted">
                            Full HD 1080p (~5 Mbps) - Best quality for most screens
                          </small>
                        </td>
                        <td>
                          {renderCopyButton(selectedStream?.hls_fhd_url || selectedStream?.hls_url?.replace('index.m3u8', 'fhd.m3u8'), 'FHD URL', 'fhd')}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">UHD 4K:</td>
                        <td>
                          <code className="bg-light px-2 py-1 rounded d-block mb-2">
                            {selectedStream?.hls_uhd_url || selectedStream?.hls_url?.replace('index.m3u8', 'uhd.m3u8')}
                          </code>
                          <small className="text-muted">
                            Ultra HD 4K (~12 Mbps) - Requires fast internet
                          </small>
                        </td>
                        <td>
                          {renderCopyButton(selectedStream?.hls_uhd_url || selectedStream?.hls_url?.replace('index.m3u8', 'uhd.m3u8'), 'UHD URL', 'uhd')}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>

              {/* Video Player */}
              <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="fas fa-play-circle me-2"></i>
                    Live Preview
                  </h5>
                  {selectedStream?.captions && selectedStream.captions.length > 0 && (
                    <Form.Select
                      size="sm"
                      value={selectedCaption}
                      onChange={handleCaptionChange}
                      style={{ width: 'auto', minWidth: '120px' }}
                    >
                      <option value="off">Captions Off</option>
                      {selectedStream.captions.map((caption, index) => (
                        <option key={index} value={index}>
                          {caption.label}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="position-relative bg-dark">
                    {playerStatus === 'unavailable' ? (
                      <StreamUnavailable />
                    ) : (
                      <video
                        ref={videoRef}
                        className="w-100"
                        style={{ height: '300px', backgroundColor: '#000' }}
                        controls
                        muted
                        playsInline
                        crossOrigin="anonymous"
                        poster="/images/video-placeholder.jpg"
                      />
                    )}
                    {selectedStream?.is_live && (
                      <Badge 
                        bg="danger" 
                        className="position-absolute top-0 start-0 m-3"
                        style={{ fontSize: '12px' }}
                      >
                        ● LIVE
                      </Badge>
                    )}
                  </div>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="fas fa-chart-line me-2"></i>
                    Live Status
                  </h5>
                </Card.Header>
                <Card.Body>
                  {selectedStream?.is_live ? (
                    <div className="text-center py-4">
                      <div className="mb-3">
                        <span className="display-1 text-success">●</span>
                      </div>
                      <h4 className="text-success">Stream is Live!</h4>
                      <p className="text-muted">
                        {selectedStream?.live_status?.clients || 0} viewers currently watching
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="mb-3">
                        <span className="display-1 text-secondary">○</span>
                      </div>
                      <h4 className="text-muted">Stream is Offline</h4>
                      <p className="text-muted">
                        Start streaming to see live preview
                      </p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">Stream Stats</h5>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <small className="text-muted d-block">Viewers</small>
                    <h4 className="mb-0">
                      {selectedStream?.viewer_count || 0}
                      {selectedStream?.is_live && (
                        <span className="text-success ms-2" style={{fontSize: '14px'}}>
                          ● Live
                        </span>
                      )}
                    </h4>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block">Bandwidth Usage</small>
                    <h4 className="mb-0">
                      {selectedStream?.bandwidth_usage 
                        ? `${(selectedStream.bandwidth_usage / 1024 / 1024).toFixed(2)} MB`
                        : '0 MB'
                      }
                    </h4>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block">Created</small>
                    <span>
                      {selectedStream?.created_at 
                        ? new Date(selectedStream.created_at).toLocaleString()
                        : '-'
                      }
                    </span>
                  </div>
                  <div className="mb-0">
                    <small className="text-muted d-block">Last Active</small>
                    <span>
                      {selectedStream?.last_active_at 
                        ? new Date(selectedStream.last_active_at).toLocaleString()
                        : 'Never'
                      }
                    </span>
                  </div>
                </Card.Body>
              </Card>

              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">Quick Actions</h5>
                </Card.Header>
                <Card.Body>
                  <Button 
                    variant="outline-primary" 
                    className="w-100 mb-2"
                    onClick={() => copyToClipboard(selectedStream?.rtmp_url, 'RTMP URL', 'rtmp')}
                  >
                    <i className="fas fa-copy me-2"></i>
                    Copy RTMP URL
                  </Button>
                  <Button 
                    variant="outline-success" 
                    className="w-100 mb-2"
                    onClick={() => copyToClipboard(selectedStream?.hls_url, 'HLS URL', 'hls')}
                  >
                    <i className="fas fa-play me-2"></i>
                    Copy HLS URL
                  </Button>
                  <Button 
                    variant="outline-info" 
                    className="w-100"
                    onClick={() => fetchStream(id)}
                  >
                    <i className="fas fa-sync-alt me-2"></i>
                    Refresh Status
                  </Button>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="fas fa-code me-2"></i>
                    Embed Code
                  </h5>
                </Card.Header>
                <Card.Body>
                  <p className="text-muted small mb-3">
                    Copy and paste this code to embed the video player on your website.
                  </p>
                  
                  <Form.Group className="mb-3">
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={generateEmbedCode()}
                      readOnly
                      className="font-monospace bg-light"
                      style={{ fontSize: '12px' }}
                    />
                  </Form.Group>

                  <div className="d-grid gap-2">
                    <Button 
                      variant="primary"
                      onClick={copyEmbedCode}
                    >
                      <i className="fas fa-copy me-2"></i>
                      Copy Embed Code
                    </Button>
                    
                    <div className="d-flex gap-2">
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        className="flex-fill"
                        onClick={() => setShowEmbedModal(true)}
                      >
                        <i className="fas fa-cog me-1"></i>
                        Customize
                      </Button>
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        className="flex-fill"
                        onClick={() => {
                          const streamName = selectedStream?.stream_name || selectedStream?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'stream';
                          window.open(`/embed-player.html?stream=${streamName}`, '_blank');
                        }}
                      >
                        <i className="fas fa-external-link-alt me-1"></i>
                        Preview
                      </Button>
                    </div>
                  </div>

                  <div className="mt-3 p-2 bg-light rounded">
                    <small className="text-muted">
                      <i className="fas fa-info-circle me-1"></i>
                      Supports responsive design, autoplay, and multiple quality options.
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="embed" title="Embedded Player">
          <Row>
            <Col lg={8}>
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="fas fa-play-circle me-2"></i>
                    Embedded Player Preview
                  </h5>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="position-relative bg-dark">
                  <iframe
                      src={`/embed-player.html?stream=${selectedStream?.stream_name || selectedStream?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'stream'}&autoplay=false`}
                      width="100%"
                      height="400"
                      frameBorder="0"
                      allow="autoplay; fullscreen"
                      style={{ backgroundColor: '#000', minHeight: '400px' }}
                      title="Embedded Stream Player"
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="fas fa-code me-2"></i>
                    Embed Code
                  </h5>
                </Card.Header>
                <Card.Body>
                  <p className="text-muted small mb-3">
                    Copy and paste this code to embed the video player on your website.
                  </p>
                  
                  <Form.Group className="mb-3">
                    <Form.Control
                      as="textarea"
                      rows={6}
                      value={generateEmbedCode()}
                      readOnly
                      className="font-monospace bg-light"
                      style={{ fontSize: '11px' }}
                    />
                  </Form.Group>

                  <div className="d-grid gap-2">
                    <Button 
                      variant="primary"
                      onClick={copyEmbedCode}
                    >
                      <i className="fas fa-copy me-2"></i>
                      Copy Embed Code
                    </Button>
                    
                    <Button 
                      variant="outline-secondary"
                      onClick={() => setShowEmbedModal(true)}
                    >
                      <i className="fas fa-cog me-2"></i>
                      Customize Embed Options
                    </Button>
                  </div>

                  <div className="mt-3 p-2 bg-light rounded">
                    <small className="text-muted">
                      <i className="fas fa-info-circle me-1"></i>
                      Supports responsive design, autoplay, and multiple quality options.
                    </small>
                  </div>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="fas fa-external-link-alt me-2"></i>
                    Direct Links
                  </h5>
                </Card.Header>
                <Card.Body>
                  <p className="text-muted small mb-3">
                    Use these links to share or embed directly.
                  </p>
                  
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Player URL</Form.Label>
                    <Form.Control
                      type="text"
                      value={`${window.location.origin}/embed-player.html?stream=${selectedStream?.stream_name || selectedStream?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'stream'}`}
                      readOnly
                      className="font-monospace bg-light"
                      style={{ fontSize: '12px' }}
                    />
                  </Form.Group>

                  <Button 
                    variant="outline-primary" 
                    className="w-100"
                    onClick={() => {
                      const streamName = selectedStream?.stream_name || selectedStream?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'stream';
                      window.open(`/embed-player.html?stream=${streamName}`, '_blank');
                    }}
                  >
                    <i className="fas fa-external-link-alt me-2"></i>
                    Open Player in New Tab
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="logs" title="Activity Logs">
          <Card>
            <Card.Body>
              <h5 className="mb-4">Recent Activity</h5>
              {selectedStream?.recent_logs && selectedStream.recent_logs.length > 0 ? (
                <div className="timeline">
                  {selectedStream.recent_logs.map((log, index) => (
                    <div key={index} className="timeline-item mb-3 pb-3 border-bottom">
                      <div className="d-flex">
                        <div className="timeline-icon me-3">
                          <i className={`fas fa-${
                            log.event_type.includes('publish') ? 'upload' :
                            log.event_type.includes('playback') ? 'play' :
                            log.event_type.includes('status') ? 'chart-line' :
                            'info-circle'
                          } text-primary`}></i>
                        </div>
                        <div>
                          <h6 className="mb-1">{log.event_type}</h6>
                          <small className="text-muted">
                            {new Date(log.created_at).toLocaleString()}
                          </small>
                          {log.event_data && (
                            <pre className="mt-2 mb-0 bg-light p-2 rounded">
                              <small>{JSON.stringify(JSON.parse(log.event_data), null, 2)}</small>
                            </pre>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  <i className="fas fa-chart-line fa-3x mb-3"></i>
                  <p>No activity logs available</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="watermark" title="Watermark">
          <StreamWatermark streamId={id} />
        </Tab>

        <Tab eventKey="social" title="Social Streaming">
          <SocialStreamingConfig
            streamId={id}
            streamName={selectedStream?.name}
          />
        </Tab>
      </Tabs>

      {/* Caption Manager Modal */}
      <CaptionManager
        show={showCaptionModal}
        onHide={() => setShowCaptionModal(false)}
        itemId={id}
        itemType="stream"
      />

      {/* Embed Code Modal */}
      {showEmbedModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-code me-2"></i>
                  Embed Player Code
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowEmbedModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <Form>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Width</Form.Label>
                        <Form.Control
                          type="text"
                          value={embedOptions.width}
                          onChange={(e) => setEmbedOptions({...embedOptions, width: e.target.value})}
                          placeholder="100% or 640"
                        />
                        <Form.Text className="text-muted">
                          Use % for responsive or px for fixed
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Height</Form.Label>
                        <Form.Control
                          type="text"
                          value={embedOptions.height}
                          onChange={(e) => setEmbedOptions({...embedOptions, height: e.target.value})}
                          placeholder="480"
                        />
                        <Form.Text className="text-muted">
                          Height in pixels (e.g., 480, 720)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Check
                        type="switch"
                        id="autoplay-switch"
                        label="Autoplay"
                        checked={embedOptions.autoplay}
                        onChange={(e) => setEmbedOptions({...embedOptions, autoplay: e.target.checked})}
                      />
                    </Col>
                    <Col md={4}>
                      <Form.Check
                        type="switch"
                        id="muted-switch"
                        label="Start Muted"
                        checked={embedOptions.muted}
                        onChange={(e) => setEmbedOptions({...embedOptions, muted: e.target.checked})}
                      />
                      <Form.Text className="text-muted d-block" style={{fontSize: '11px'}}>
                        Required for autoplay
                      </Form.Text>
                    </Col>
                    <Col md={4}>
                      <Form.Check
                        type="switch"
                        id="logo-switch"
                        label="Show Logo"
                        checked={embedOptions.logo}
                        onChange={(e) => setEmbedOptions({...embedOptions, logo: e.target.checked})}
                      />
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Embed Code</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={6}
                      value={generateEmbedCode()}
                      readOnly
                      className="font-monospace bg-light"
                    />
                  </Form.Group>

                  <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    <strong>Tip:</strong> For responsive embed, use width="100%" and wrap in a container with aspect ratio:
                    <pre className="mt-2 mb-0 small">
{`<div style="position: relative; padding-bottom: 56.25%; height: 0;">
  <iframe src="..." style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;">
  </iframe>
</div>`}
                    </pre>
                  </div>
                </Form>
              </div>
              <div className="modal-footer">
                <Button variant="secondary" onClick={() => setShowEmbedModal(false)}>
                  Close
                </Button>
                <Button variant="primary" onClick={copyEmbedCode}>
                  <i className="fas fa-copy me-2"></i>
                  Copy Code
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  selectedStream: state.streams.selectedStream,
  loading: state.streams.loading,
  error: state.streams.error,
  updating: state.streams.updating,
  regenerating: state.streams.regenerating,
  isAdmin: selectIsAdmin(state),
});

const mapDispatchToProps = {
  fetchStream,
  updateStream,
  fetchStreams,
  regenerateStreamKey
};

export default connect(mapStateToProps, mapDispatchToProps)(StreamDetail);
